import React from 'react';
import { Box, Typography, Button, Paper } from '@mui/material';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import RefreshIcon from '@mui/icons-material/Refresh';

/**
 * ErrorBoundary — catches render-time errors inside GameLevel (mic/audio API failures,
 * unexpected null refs, etc.) and surfaces a friendly recovery screen instead of
 * white-screening the whole app.
 *
 * Usage:
 *   <ErrorBoundary onReset={callback}>
 *     <GameLevel ... />
 *   </ErrorBoundary>
 */
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    console.error('[ErrorBoundary] Caught error:', error, info);
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null });
    if (this.props.onReset) this.props.onReset();
  };

  render() {
    if (!this.state.hasError) return this.props.children;

    const isMicError =
      this.state.error?.message?.toLowerCase().includes('audio') ||
      this.state.error?.message?.toLowerCase().includes('media') ||
      this.state.error?.message?.toLowerCase().includes('mic');

    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '60vh',
          px: 3,
        }}
      >
        <Paper
          elevation={3}
          sx={{
            p: 4,
            maxWidth: 420,
            textAlign: 'center',
            borderRadius: 3,
          }}
        >
          <ErrorOutlineIcon sx={{ fontSize: 60, color: '#f44336', mb: 2 }} />
          <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 1 }}>
            {isMicError ? 'Microphone Error' : 'Something went wrong'}
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            {isMicError
              ? 'Could not access your microphone. Please check that microphone permission is granted in your device Settings, then try again.'
              : 'An unexpected error occurred. This has been noted. Tap below to go back and try again.'}
          </Typography>
          {isMicError && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3, fontStyle: 'italic' }}>
              iOS: Settings → Privacy & Security → Microphone → Every Voice Can Sing ✓
            </Typography>
          )}
          <Button
            variant="contained"
            startIcon={<RefreshIcon />}
            onClick={this.handleReset}
            sx={{ backgroundColor: '#58CC02', '&:hover': { backgroundColor: '#46a302' } }}
          >
            Try Again
          </Button>
        </Paper>
      </Box>
    );
  }
}

export default ErrorBoundary;
